import { AlertTriangle, CheckCircle2, MessageSquarePlus } from "lucide-react";
import { useMemo, type FC } from "react";

import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import { useDashboardDraft } from "@/features/dashboard/useDashboardDraft";
import { cn } from "@/lib/utils";

import { useClientProfile } from "../context/ClientProfileContext";
import {
  gapLabel,
  type Coverage,
  type Gap,
  type HistoryKind
} from "../lib/coverage";
import { getFullName } from "../lib/format";

const NOUN: Record<HistoryKind, string> = {
  address: "addresses",
  employment: "employment"
};

/** Asks the Client Manager to draft a short request for just the missing period. */
export const useAskForGaps = (kind: HistoryKind) => {
  const { clientId, client } = useClientProfile();
  const name = client ? getFullName(client) || "the client" : "the client";
  const scope = useMemo(
    () => ({ kind: "client" as const, id: clientId, displayName: name }),
    [clientId, name]
  );
  const draft = useDashboardDraft("client", scope);
  return {
    isSending: draft.isSending,
    ask: (gaps: Gap[]) =>
      draft.send(
        `Draft a short request to ${name} asking only for their ${
          kind === "address"
            ? "home addresses"
            : "employment (or what they were doing, if not working)"
        } for ${gaps
          .map(gapLabel)
          .join(
            ", "
          )}. Don't resend the full intake; ask just for these periods, with dates.`
      )
  };
};

type Props = { kind: HistoryKind; coverage: Coverage | null };

/** "4 years 2 months of 5 years covered" with the gaps and a targeted request. */
export const CoverageBar: FC<Props> = ({ kind, coverage }) => {
  const { ask, isSending } = useAskForGaps(kind);
  if (!coverage) return null;
  const { target, ratio, gaps, coveredLabel } = coverage;
  const full = gaps.length === 0;
  return (
    <div
      className="flex flex-col gap-2 border-b border-line-1 px-3 py-2.5 sm:flex-row sm:items-center sm:gap-4"
      data-testid={`coverage-${kind}`}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex flex-wrap items-baseline gap-x-2 text-base">
          {full ? (
            <CheckCircle2 className="size-3.5 self-center text-success" />
          ) : (
            <AlertTriangle className="size-3.5 self-center text-warn" />
          )}
          <span className="font-medium">
            {full
              ? `${target.span} of ${NOUN[kind]} covered`
              : `${coveredLabel} of ${target.span.toLowerCase()} covered`}
          </span>
          <span className="text-sm text-fg-3">
            {target.form
              ? `${target.form} asks for ${target.span.toLowerCase()}`
              : "Most forms ask for 5 years"}
          </span>
        </div>
        <div
          className="h-1.5 w-full max-w-md overflow-hidden rounded-full bg-fill-2"
          role="progressbar"
          aria-valuenow={Math.round(ratio * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${NOUN[kind]} coverage`}
        >
          <div
            className={cn(
              "h-full rounded-full",
              full ? "bg-success" : "bg-warn"
            )}
            style={{ width: `${Math.round(ratio * 100)}%` }}
          />
        </div>
        {!full ? (
          <span className="text-sm text-fg-3">
            Missing: {gaps.map(gapLabel).join(", ")}
          </span>
        ) : null}
      </div>
      {!full ? (
        <Button
          variant="secondary"
          size="xs"
          disabled={isSending}
          onClick={() => void ask(gaps)}
          data-testid={`coverage-ask-${kind}`}
        >
          <MessageSquarePlus /> Ask the Client for the Missing Period
        </Button>
      ) : null}
    </div>
  );
};

/** A gap between rows, inline in the history table. */
export const GapRow: FC<{
  gap: Gap;
  columns: number;
  /** Which column holds the dates. */
  datesColumn: number;
  kind: HistoryKind;
}> = ({ gap, columns, datesColumn, kind }) => {
  const { ask, isSending } = useAskForGaps(kind);
  const months = Math.max(1, Math.round(gap.days / 30.44));
  const cells = Array.from({ length: columns }, (_, i) => {
    if (i === 0)
      return (
        <TableCell key="label" className="h-12">
          <div className="flex items-center gap-2">
            <AlertTriangle className="size-3.5 shrink-0 text-warn" />
            <span className="text-base font-medium text-warn">
              Gap · {months} month{months === 1 ? "" : "s"}
            </span>
          </div>
        </TableCell>
      );
    if (i === datesColumn)
      return (
        <TableCell key="dates" className="h-12">
          <span className="text-sm text-fg-3">{gapLabel(gap)}</span>
        </TableCell>
      );
    if (i === columns - 1)
      return (
        <TableCell key="ask" className="h-12">
          <Button
            variant="ghost"
            size="xs"
            disabled={isSending}
            onClick={() => void ask([gap])}
          >
            Ask
          </Button>
        </TableCell>
      );
    return <TableCell key={`blank-${String(i)}`} className="h-12" />;
  });
  return (
    <TableRow
      className="bg-warn-soft hover:bg-warn-soft"
      data-testid="coverage-gap-row"
    >
      {cells}
    </TableRow>
  );
};
